
import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Button,
    Chip,
    Box,
    TextField,
    MenuItem,
    Select,
    FormControl,
    Autocomplete,
    Checkbox,
    IconButton,
    InputLabel,
    Tooltip
} from '@mui/material';
import RestAPIService from '../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../Utility/API/Serviceurls';
import { CommonPatientDetails } from '../../Components/Common Components/CommonComponents';
import Loader from '../../Components/Loader';
import ToastMsg from '../../Components/ToastMsg/ToastMsg';
import {ImagePaths} from '../../Utility/ImagePaths';

const HistopathSpecimen = ({ open, onClose, errorMessage, patientId, selectedDate }) => {

    const [specimenList, setSpecimenList] = useState([]);
    const [testData, setTestData] = useState([]);
    const [patientInfo, setPatientInfo] = useState({});
    const [existingSamples, setExistingSamples] = useState({});
    const [selectedTests, setSelectedTests] = useState(new Set());
    const [savedTests, setSavedTests] = useState(new Set());
    const [isloader, setisloader] = useState(false);
    const [message, setMessage] = useState({
        isErrorMsgText: '',
        successMsgText: '',
        isErrorMsg: false,
        successMsg: false,
    })

    // Sample type options
    const sampleTypeOptions = [
        { value: 'New', label: 'New' },
        { value: 'Existing', label: 'Existing' },
    ];

    const successMessage = (message) => {
        setMessage({
            ...message,
            successMsg: true,
            successMsgText: message,
        })
    }

    const msgClose = () => {
        setMessage({
            ...message,
            isErrorMsg: false,
            isErrorMsgText: '',
            successMsg: false,
            successMsgText: ''
        })
    }


    useEffect(() => {
        if (open && patientId) {
            getSpecimenList();
            getSampleCollectionData();
        } else {
            // Reset data when dialog closes
            setTestData([]);
            setPatientInfo({});
            setExistingSamples({});
            setSelectedTests(new Set());
        }
    }, [open, patientId, selectedDate]);

    const getSpecimenList = () => {
        try {
            RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_SPECIMEN + `?is_active=true`)
                .then((response) => {
                    if (response.data.status === 'success') {
                        setSpecimenList(response.data.data?.filter((item) => item?.is_active) || []);
                    } else {
                        errorMessage(response.data.message);
                    }
                }).catch(e => {
                    if (e?.response?.data?.status === 'fail') {
                        errorMessage(e.response.data.message);
                    } else {
                        errorMessage(e.message);
                    }
                });
        } catch (e) {
            errorMessage(e.message);
        }
    };

    const getSampleCollectionData = () => {
        if (!patientId) {
            errorMessage('Patient ID is required');
            return;
        }

        setisloader(true);
        try {
            // Format date if provided
            let formattedDate = '';
            if (selectedDate) {
                const date = new Date(selectedDate);
                formattedDate = date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
            }

            let queryString = `?patient_id=${patientId}`;
            if (formattedDate) {
                queryString += `&date=${formattedDate}`;
            }

            RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_SAMPLE_DETAILS + queryString)
                .then((response) => {
                    if (response.data.status === 'success') {
                        const testDatas = response.data.test_datas || [];
                        setTestData(testDatas);
                        setPatientInfo(response.data.patient_data || {});
                        const initialSelected = new Set();
                        testDatas.forEach((invoice, invoiceIndex) => {
                            invoice.tests.forEach((test, testIndex) => {
                                const testKey = `${invoiceIndex}-${testIndex}`;
                                if (test.specimen_id) {
                                    initialSelected.add(testKey);
                                }
                            });
                        });
                        setSelectedTests(initialSelected);
                    } else {
                        errorMessage(response.data.message);
                        setTestData([]);
                        setPatientInfo({});
                    }
                }).catch(e => {
                    if (e?.response?.data?.status === 'fail') {
                        errorMessage(e.response.data.message);
                    } else {
                        errorMessage(e.message);
                    }
                    setTestData([]);
                    setPatientInfo({});
                }).finally(() => {
                    setisloader(false);
                });
        } catch (e) {
            errorMessage(e.message);
            setisloader(false);
        }
    };

    const getExistingSamples = (specimenId) => {
        if (!specimenId || !patientId) return;

        try {

            RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_SAMPLE_LIST + `?patient_id=${patientId}&specimen_id=${specimenId}`)
                .then((response) => {
                    if (response.data.status === 'success') {
                        setExistingSamples(prev => ({
                            ...prev,
                            [specimenId]: response.data.data || []
                        }));
                    } else {
                        errorMessage(response.data.message);
                    }
                }).catch(e => {
                    if (e?.response?.data?.status === 'fail') {
                        errorMessage(e.response.data.message);
                    } else {
                        errorMessage(e.message);
                    }
                });
        } catch (e) {
            errorMessage(e.message);
        }
    };

    // Handle test selection (checkbox)
    const handleTestSelection = (invoiceIndex, testIndex, isSelected) => {

        const testKey = `${invoiceIndex}-${testIndex}`;
        const test = testData[invoiceIndex]?.tests[testIndex];
         if (isSelected && (savedTests.has(testKey) || (test.specimen_id && test.sample_collected))) {
        return;
        }
        const newSelectedTests = new Set(selectedTests);

        if (isSelected) {
            newSelectedTests.add(testKey);
        } else {
            newSelectedTests.delete(testKey);
            // Clear all fields when test is unchecked
            const updatedTestData = [...testData];
            const testToClear = updatedTestData[invoiceIndex].tests[testIndex];
            testToClear.specimen_id = null;
            testToClear.specimen_data = null;
            testToClear.sample_type = null;
            testToClear.sample_id = null;
            testToClear.location = null;
            testToClear.overall_specimen_sequence = null;
            testToClear.select_is_outsource = testToClear.is_outsource ? 'Outsource' : 'Self';
            setTestData(updatedTestData);
        }

        setSelectedTests(newSelectedTests);
    };

    // Check if test is selected and editable
    const isTestSelected = (invoiceIndex, testIndex) => {
        return selectedTests.has(`${invoiceIndex}-${testIndex}`);
    };

    // Check if test has existing specimen data (should be disabled)
    const hasExistingSpecimen = (test) => {
        return test.sample_collected;
    };

    // Check if checkbox should be disabled (has existing specimen data)
    const isCheckboxDisabled = (test, invoiceIndex, testIndex) => {
        const testKey = `${invoiceIndex}-${testIndex}`;
        return savedTests.has(testKey) || (test.specimen_id && test.sample_collected);
    };

    const handleSampleTypeChange = (invoiceIndex, testIndex, value) => {
        if (!isTestSelected(invoiceIndex, testIndex) || hasExistingSpecimen(testData[invoiceIndex]?.tests[testIndex])) return;

        const updatedTestData = [...testData];
        const test = updatedTestData[invoiceIndex].tests[testIndex];

        test.sample_type = value;

        if (value === 'Existing' && test.specimen_id) {
            getExistingSamples(test.specimen_id);

            test.sample_id = null;
            test.location = null;
            test.overall_specimen_sequence = null;
        } else if (value === 'New') {
            test.sample_id = null;
            test.location = null;
            test.overall_specimen_sequence = null;
        }
        setTestData(updatedTestData);
    };

    const handleSpecimenChange = (invoiceIndex, testIndex, value) => {
        if (!isTestSelected(invoiceIndex, testIndex) || hasExistingSpecimen(testData[invoiceIndex]?.tests[testIndex])) return;

        const updatedTestData = [...testData];
        const test = updatedTestData[invoiceIndex].tests[testIndex];

        test.specimen_id = value?.id || null;
        test.specimen_data = value;
        setTestData(updatedTestData);

        if (test.sample_type === 'Existing' && value?.id) {
            getExistingSamples(value.id);

            test.sample_id = null;
            test.location = null;
            test.overall_specimen_sequence = null;
        }
    };

    const handleSampleIdChange = (invoiceIndex, testIndex, value) => {
        if (!isTestSelected(invoiceIndex, testIndex) || hasExistingSpecimen(testData[invoiceIndex]?.tests[testIndex])) return;

        const updatedTestData = [...testData];
        const test = updatedTestData[invoiceIndex].tests[testIndex];

        if (value && test.specimen_id && test.sample_type === 'Existing') {
            const existingSampleList = existingSamples[test.specimen_id] || [];
            const selectedSample = existingSampleList.find(sample =>
                sample.sample_id === value
            );

            if (selectedSample) {
                updatedTestData[invoiceIndex].tests[testIndex] ={
                ...test,
                sample_id: selectedSample.sample_id,
                location: selectedSample.location,
                overall_specimen_sequence: selectedSample.overall_specimen_sequence,
            }
        }
        } else {
            test.sample_id = value;
            test.location = null;
            test.overall_specimen_sequence = null;
        }
        setTestData(updatedTestData);
    };

    const handleLocationChange = (invoiceIndex, testIndex, value) => {
        if (!isTestSelected(invoiceIndex, testIndex) || hasExistingSpecimen(testData[invoiceIndex]?.tests[testIndex])) return;

        const updatedTestData = [...testData];
        updatedTestData[invoiceIndex].tests[testIndex].location = value;
        setTestData(updatedTestData);
    };

    const handleLaboratoryChange = (invoiceIndex, testIndex, value) => {
        if (!isTestSelected(invoiceIndex, testIndex) || hasExistingSpecimen(testData[invoiceIndex]?.tests[testIndex])) return;

        const updatedTestData = [...testData];
        updatedTestData[invoiceIndex].tests[testIndex].select_is_outsource = value;
        setTestData(updatedTestData);
    };

    const handleSave = () => {
        setisloader(true);

        try {
            const data = testData.flatMap(invoice =>
                invoice.tests
                    .filter(test => {
                        const testKey = `${testData.indexOf(invoice)}-${invoice.tests.indexOf(test)}`;
                        const isSelected = selectedTests.has(testKey);
                        const hasRequiredFields = test.specimen_id && test.sample_type;
                        const isNotCollected = !test.sample_collected; 
                        const isValidExistingSample = test.sample_type !== 'Existing' || (test.sample_type === 'Existing' && test.sample_id);
                        // const isValidLocation = test.sample_type !== 'New' || (test.sample_type === 'New' && test.location && test.location.trim() !== '');isValidLocation

                        return isSelected && hasRequiredFields && isNotCollected && isValidExistingSample;
                    })
                    .map(test => ({
                        specimen_id: test.specimen_id,
                        sample_type: test.sample_type,
                        sample_id: test.sample_id,
                        location: test.location,
                        overall_specimen_sequence: test.overall_specimen_sequence,
                        result_item_id: test.result_item_id,
                        is_outsource: test.select_is_outsource === 'Outsource' ? true : 
                                        test.select_is_outsource === 'Self' ? false : 
                                        test.is_outsource,
                    }))
                    .filter(test => test.specimen_id && test.sample_type)
            );

            if (data.length === 0) {
                errorMessage('Please select value in Sample Type, all required fields.');
                setisloader(false);
                return;
            }

            RestAPIService.create(data, Serviceurls.HISTOPATHOLOGY_SAMPLE_DETAILS + `?patient_id=${patientId}`)
                .then((response) => {
                    if (response.data.status === 'success') {
                        successMessage(response.data.message)

                        const newSavedTests = new Set(savedTests);
                        testData.forEach((invoice, invoiceIndex) => {
                            invoice.tests.forEach((test, testIndex) => {
                                const testKey = `${invoiceIndex}-${testIndex}`;
                                if (selectedTests.has(testKey) && test.specimen_id && test.sample_type) {
                                    newSavedTests.add(testKey);
                                }
                            });
                        });
                        setSavedTests(newSavedTests);
                        getSampleCollectionData();
                    } else {
                        errorMessage('Failed to save: ' + response.data.message);
                    }
                }).catch(e => {
                    if (e?.response?.data?.status === 'fail') {
                        errorMessage(e.response.data.message);
                    } else {
                        errorMessage(e.message);
                    }
                }).finally(() => {
                    setisloader(false);
                });
        } catch (e) {
            errorMessage(e.message);
            setisloader(false);
        }
    };

    const handleBarcode = (invoiceIndex, testIndex) => {
    const test = testData[invoiceIndex]?.tests[testIndex];
    try {
        setisloader(true);
        RestAPIService.getAllPrint(Serviceurls.HISTOPATHOLOGY_SPECIMEN_BARCODE + `?barcode_id=${test.barcode_id}`)
            .then((response) => {
                 const file = new Blob(
                        [response.data],
                        { type: 'application/pdf' });
                    const fileURL = URL.createObjectURL(file);
                     window.open(fileURL);
            }).catch((e) => {
                    errorMessage(e.message);
                
            }).finally(() => {
                setisloader(false);
            });
    } catch (e) {
        errorMessage(e.message);
        setisloader(false);
    }
};

    // Find the current specimen value for Autocomplete
    const getCurrentSpecimenValue = (test) => {
        if (test.specimen_id) {
            const specimenlist = specimenList.find(specimen => specimen.id === test.specimen_id) || null;
            return specimenlist;
        }
        return null;
    };

    // Get existing sample options for a test
    const getExistingSampleOptions = (test) => {
        if (test.sample_type !== 'Existing' || !test.specimen_id) {
            return [];
        }
        return existingSamples[test.specimen_id] || [];
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    minHeight: '80vh',
                    minWidth: "90dvw"
                }
            }}
        >
            <DialogTitle sx={{
                backgroundColor: '#f5f5f5',
                borderBottom: '1px solid #e0e0e0',
                pb: 1
            }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                    Sample Collection
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {/* Patient Information Section */}
                <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            <CommonPatientDetails data={patientInfo} />
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Total No. of test: {patientInfo.total_no_of_test || 0}
                        </Typography>
                    </Box>
                </Box>

                {/* Loading State */}
                {(isloader)}

                {/* Test Data Sections */}
                {!isloader && testData.length === 0 && (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography>No test data found for this patient.</Typography>
                    </Box>
                )}

                {!isloader && testData.map((invoiceData, invoiceIndex) => (
                    <Box key={invoiceIndex} sx={{ p: '11px', borderBottom: '1px solid #e0e0e0' }}>
                        {/* Header */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2
                        }}
                        >
                            <Box sx={{ display: 'flex', justifyContent:'space-between', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body1" sx={{width:'28vw'}} >
                                    Bill No: <strong>{invoiceData.invoice_no}</strong>
                                </Typography>
                                <Typography sx={{width:'29vw'}}>
                                    Payment Status: <strong>{invoiceData.payment_status && (
                                        invoiceData.payment_status.charAt(0).toUpperCase()+
                                        invoiceData.payment_status.slice(1).toLowerCase()
                                    )}</strong>
                                </Typography>
                                <Typography variant="body2">
                                    No Of Test: <strong>{invoiceData.no_of_test}</strong>
                                </Typography>
                            </Box>
                        </Box>

                        {/* Table */}
                        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', Overflow: 'hidden', width: '100%' }}>
                            <Table sx={{ minWidth: '100%', tableLayout: 'fixed', Overflow: 'hidden' }} size="small">
                                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', width: '5vw' }}>Select</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', width: '7vw' }}>Test Name</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', width: '12vw' }}>Specimen</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', width: '10vw' }}>Sample type</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', width: '11vw' }}>Sample ID</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', width: '10vw' }}>Location</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', width: '8vw' }}>Overall Specimen Sequence</TableCell>
                                        {invoiceData.tests.some(test => test.is_outsource !== undefined) && (
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', width: '6vw', }}>Type</TableCell>
                                        )}
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', width: '7vw' }}>Barcode</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoiceData.tests.map((test, testIndex) => {
                                        const existingSampleOptions = getExistingSampleOptions(test);
                                        const isExistingSample = test.sample_type === 'Existing';
                                        const isSelected = isTestSelected(invoiceIndex, testIndex);
                                        const hasSpecimen = hasExistingSpecimen(test);
                                        const isCheckboxDisabledFlag = isCheckboxDisabled(test);
                                        const isFieldsDisabled = !isSelected || isCheckboxDisabled(test, invoiceIndex, testIndex);
                                        const isSampleIdDisabled = isFieldsDisabled || !isExistingSample;
                                        const isLaboratoryDisabled = !isSelected || isCheckboxDisabled(test, invoiceIndex, testIndex);

                                        return (
                                            <TableRow key={testIndex}>
                                                {/* Checkbox for test selection */}
                                                <TableCell sx={{ fontSize: '0.875rem', width: '5vw', }}>
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={(e) => handleTestSelection(invoiceIndex, testIndex, e.target.checked)}
                                                        disabled={isCheckboxDisabled(test, invoiceIndex, testIndex)}
                                                        color="primary"
                                                        sx={{ padding: '4px' }}
                                                    />
                                                </TableCell>

                                                <TableCell sx={{
                                                    fontSize: '0.875rem',
                                                    width: '7vw',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {test.testName}
                                                </TableCell>

                                                {/* Specimen Autocomplete */}
                                                <TableCell sx={{ fontSize: '0.875rem', width: '12vw', }}>
                                                    <Autocomplete
                                                        size="small"
                                                        options={specimenList}
                                                        getOptionLabel={(option) => option?.specimen_type || ''}
                                                        value={getCurrentSpecimenValue(test)}
                                                        onChange={(event, newValue) => {
                                                            handleSpecimenChange(invoiceIndex, testIndex, newValue);
                                                        }}
                                                        disabled={isFieldsDisabled}
                                                        renderInput={(params) => (
                                                            <Tooltip title={params.inputProps.value || ''}>
                                                            <TextField
                                                                {...params}
                                                                label="Select Specimen *"
                                                                sx={{ width: "100%", padding: '0px' }}
                                                                size="small"
                                                            />
                                                            </Tooltip>
                                                        )}
                                                        isOptionEqualToValue={(option, value) => option.id === value?.id || option?.id?.toString() === value?.id?.toString()}
                                                    />
                                                </TableCell>

                                                {/* Sample Type Dropdown */}
                                                <TableCell sx={{ fontSize: '0.875rem', width: '10vw', }}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel id={`sample-type-label-${invoiceIndex}-${testIndex}`} sx={{ fontSize: '0.875rem' }}>Sample Type *</InputLabel>
                                                        <Select
                                                            labelId={`sample-type-label-${invoiceIndex}-${testIndex}`}
                                                            value={test.sample_type || ''}
                                                            label="Sample Type *"
                                                            onChange={(e) => handleSampleTypeChange(invoiceIndex, testIndex, e.target.value)}
                                                            displayEmpty
                                                            disabled={isFieldsDisabled}
                                                            sx={{ fontSize: '0.875rem' }}
                                                        >
                                                            {sampleTypeOptions.map((option) => (
                                                                <MenuItem key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>

                                                {/* Sample ID - TextField for New, Autocomplete for Existing */}
                                                <TableCell sx={{ fontSize: '0.875rem', width: '11vw', }}>
                                                    {isExistingSample ? (
                                                        <Autocomplete
                                                            size="small"
                                                            options={existingSampleOptions}
                                                            getOptionLabel={(option) => option.sample_id || ''}
                                                            value={existingSampleOptions.find(sample => sample.sample_id === test.sample_id) || null}
                                                            onChange={(event, newValue) => {
                                                                handleSampleIdChange(invoiceIndex, testIndex, newValue?.sample_id);
                                                            }}
                                                            disabled={test.sample_type === 'New' || isFieldsDisabled}
                                                            renderInput={(params) => (
                                                                <Tooltip placement='top' title={params.inputProps.value || ''} arrow>
                                                                <TextField
                                                                    {...params}
                                                                    label="Sample ID*"
                                                                    sx={{ width: "100%",padding: '0px' }}
                                                                    size="small"
                                                                />
                                                                </Tooltip>
                                                            )}
                                                            isOptionEqualToValue={(option, value) => option.sample_id === value?.sample_id}
                                                        />
                                                    ) : (
                                                        <Tooltip placement='top' title={test.sample_id || ''} arrow>
                                                        <TextField
                                                            size="small"
                                                            value={test.sample_id || ''}
                                                            onChange={(e) => handleSampleIdChange(invoiceIndex, testIndex, e.target.value)}
                                                            placeholder="Enter Sample ID"
                                                            disabled={isSampleIdDisabled}
                                                            sx={{
                                                                fontSize: '0.875rem',
                                                                width: '100%',
                                                                '& .MuiInputBase-input': {
                                                                    height: '40px',
                                                                    padding: '0 8px'
                                                                }
                                                            }}
                                                        />
                                                        </Tooltip>
                                                    )}
                                                </TableCell>

                                                {/* Location TextField */}
                                                <TableCell sx={{ fontSize: '0.875rem', width: '10vw', }}>
                                                    <TextField
                                                        size="small"
                                                        value={test.location || ''}
                                                        onChange={(e) => handleLocationChange(invoiceIndex, testIndex, e.target.value)}
                                                        placeholder="Enter Location"
                                                        disabled={isFieldsDisabled}
                                                        sx={{
                                                            fontSize: '0.875rem',
                                                            width: '100%',
                                                            '& .MuiInputBase-input': {
                                                                height: '40px',
                                                                padding: '0 8px'
                                                            }
                                                        }}
                                                    />
                                                </TableCell>

                                                <TableCell sx={{
                                                    fontSize: '0.875rem',
                                                    width: '8vw',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    <Tooltip placement='top' title={test.overall_specimen_sequence ? test.overall_specimen_sequence : '-'} arrow>
                                                    {test.overall_specimen_sequence ? test.overall_specimen_sequence : '-'}
                                                    </Tooltip>
                                                </TableCell>

                                                {invoiceData.tests.some(test => test.is_outsource !== undefined) && (
                                                    <TableCell sx={{ fontSize: '0.875rem', width: '8vw', padding: '0vw' }}>
                                                        <FormControl fullWidth size="small">
                                                            <Select
                                                                value={test.select_is_outsource || (test.is_outsource ? 'Outsource' : 'Self') || ''} //test.select_is_outsource ||
                                                                onChange={(e) => {
                                                                    handleLaboratoryChange(invoiceIndex, testIndex, e.target.value);
                                                                }}
                                                                displayEmpty
                                                                disabled={isLaboratoryDisabled}
                                                                sx={{ fontSize: '0.875rem', height: '40px' }}
                                                            >
                                                                <MenuItem value=""></MenuItem>
                                                                <MenuItem value="Self">Self</MenuItem>
                                                                {test?.is_outsource ? <MenuItem value="Outsource">Outsource</MenuItem> : null}
                                                            </Select>
                                                        </FormControl>
                                                    </TableCell>
                                                )}
                                                <TableCell sx={{
                                                    fontSize: '0.875rem',
                                                    width: '6vw',
                                                    textAlign: 'center'
                                                }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleBarcode(invoiceIndex, testIndex)}
                                                        disabled={!test.sample_collected}
                                                        sx={{
                                                            padding: '4px',
                                                            '&:disabled': {
                                                                opacity: 0.5,
                                                                cursor: 'not-allowed'
                                                            }
                                                        }}
                                                    >
                                                        <img src={ImagePaths.PrintIcons.default} alt="barcode" style={{ width: '20px', height: '20px' }} />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                ))}
            </DialogContent>

            <DialogActions sx={{
                p: 2,
                backgroundColor: '#f5f5f5',
                borderTop: '1px solid #e0e0e0',
                gap: 1
            }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={isloader}
                    sx={{textTransform:'capitalize'}}
                    size='small'
                >
                    Close
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{textTransform:'capitalize'}}
                    size='small'
                    disabled={selectedTests.size === 0 || isloader}
                >
                    {isloader ? 'Save' : 'Save'}
                </Button>
            </DialogActions>
            {message.successMsg ?
                <ToastMsg
                    severity={'success'}
                    msg={message.successMsgText}
                    msgPop={msgClose}
                />
                : null}
            <Loader loaderOpen={isloader} />
        </Dialog>
    );
};

export default HistopathSpecimen;
